import { useState, useEffect } from 'react';
import {
  Layout,
  Table,
  message,
  Spin,
  Button,
  Tag,
  Space,
  Input
} from 'antd';
import {
  BarChartOutlined,
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons';

import AdminSidebar from '../components/Sidebar';
import AppHeader from '../components/Header';

const { Content } = Layout;
const { Search } = Input;

export default function RapidApiUserList() {
  const [collapsed, setCollapsed] = useState(false);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      return;
    }

    const search = searchQuery.toLowerCase();

    const filtered = users.filter((user) =>
      String(user.id)
        .toLowerCase()
        .includes(search) ||
      String(user.rapidapi_username)
        .toLowerCase()
        .includes(search) ||
      String(user.rapidapi_subscription)
        .toLowerCase()
        .includes(search) ||
      String(user.status)
        .toLowerCase()
        .includes(search)
    );

    setFilteredUsers(filtered);
  }, [searchQuery, users]);

  const fetchUsers = async () => {
    setLoading(true);

    try {
      const myHeaders = new Headers();

      const token = sessionStorage.getItem('access_token');

      if (token) {
        myHeaders.append(
          'Authorization',
          `Bearer ${token}`
        );
      }

      const requestOptions = {
        method: 'GET',
        headers: myHeaders,
        redirect: 'follow'
      };

      const response = await fetch(
        'https://api.app.exellius.com/api/v1/rapidapi/user',
        requestOptions
      );

      const result = await response.json();

      console.log('RapidAPI users response:', result);

      if (response.ok && result.success) {
        const userList = Array.isArray(result.users)
          ? result.users
          : [];

        setUsers(userList);
        setFilteredUsers(userList);
      } else {
        message.error(
          result.message ||
            'Failed to fetch RapidAPI users'
        );

        setUsers([]);
        setFilteredUsers([]);
      }
    } catch (error) {
      console.error(
        'Error fetching RapidAPI users:',
        error
      );

      message.error(
        'An error occurred while fetching RapidAPI users'
      );

      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalytics = (user) => {
    if (!user?.id) {
      message.error(
        'RapidAPI user ID is not available'
      );
      return;
    }

    window.location.href =
      `/apiv1/analytics?rapid_id=${encodeURIComponent(
        user.id
      )}`;
  };

  const formatDate = (date) => {
    if (!date) {
      return '-';
    }

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return '-';
    }

    return parsedDate.toLocaleString();
  };

  const formatNumber = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return '0';
    }

    return Number(value).toLocaleString();
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 110,
      render: (id) => (
        <span style={{ fontWeight: 500 }}>
          {id}
        </span>
      )
    },

    {
      title: 'RapidAPI Username',
      dataIndex: 'rapidapi_username',
      key: 'rapidapi_username',
      width: 240,
      render: (username) => (
        <span style={{ fontWeight: 500 }}>
          {username}
        </span>
      )
    },

    {
      title: 'Subscription',
      dataIndex: 'rapidapi_subscription',
      key: 'rapidapi_subscription',
      width: 140,
      render: (subscription) => {
        let color = 'blue';

        if (subscription === 'BASIC') {
          color = 'blue';
        } else if (subscription === 'PRO') {
          color = 'purple';
        } else if (subscription === 'ULTRA') {
          color = 'gold';
        }

        return (
          <Tag color={color}>
            {subscription}
          </Tag>
        );
      }
    },

    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag
          color={
            status?.toLowerCase() === 'active'
              ? 'success'
              : 'error'
          }
        >
          {status}
        </Tag>
      )
    },

    {
      title: 'Credit Balance',
      dataIndex: 'credit_balance',
      key: 'credit_balance',
      width: 150,
      align: 'right',
      render: (credits) => (
        <span
          style={{
            fontWeight: 600
          }}
        >
          {formatNumber(credits)}
        </span>
      )
    },

    {
      title: 'Total Credits',
      dataIndex: 'total_credits',
      key: 'total_credits',
      width: 140,
      align: 'right',
      render: (credits) =>
        formatNumber(credits)
    },

    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 190,
      render: (date) =>
        formatDate(date)
    },

    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 190,
      render: (date) =>
        formatDate(date)
    },

    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 140,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<BarChartOutlined />}
          onClick={() =>
            handleAnalytics(record)
          }
        >
          Analytics
        </Button>
      )
    }
  ];

  return (
    <Layout
      style={{
        minHeight: '100vh'
      }}
    >
      <AdminSidebar
        collapsed={collapsed}
        onCollapse={setCollapsed}
      />

      <Layout className="site-layout">
        <AppHeader />

        <Content
          style={{
            margin: '24px 16px'
          }}
        >
          <div
            style={{
              background: '#fff',
              padding: 24,
              borderRadius: 8,
              minHeight: 'calc(100vh - 120px)'
            }}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 20,
                gap: 16,
                flexWrap: 'wrap'
              }}
            >
              <div>
                <h2
                  style={{
                    margin: 0,
                    marginBottom: 5
                  }}
                >
                  RapidAPI Users
                </h2>

                <span
                  style={{
                    color: '#8c8c8c'
                  }}
                >
                  Manage and view all RapidAPI subscribers
                </span>
              </div>

              <Space>
                <Search
                  placeholder="Search RapidAPI users..."
                  allowClear
                  prefix={
                    <SearchOutlined />
                  }
                  value={searchQuery}
                  onChange={(e) =>
                    setSearchQuery(
                      e.target.value
                    )
                  }
                  style={{
                    width: 280
                  }}
                />

                <Button
                  icon={
                    <ReloadOutlined />
                  }
                  onClick={fetchUsers}
                  loading={loading}
                >
                  Refresh
                </Button>
              </Space>
            </div>

            {/* User Table */}
            {loading ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 300
                }}
              >
                <Spin size="large" />
              </div>
            ) : (
              <Table
                dataSource={filteredUsers}
                columns={columns}
                rowKey="id"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: [
                    '10',
                    '20',
                    '50',
                    '100'
                  ],
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} users`
                }}
                scroll={{
                  x: 1300,
                  y: 'calc(100vh - 280px)'
                }}
                locale={{
                  emptyText:
                    searchQuery
                      ? 'No users found matching your search'
                      : 'No RapidAPI users found'
                }}
              />
            )}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}

